import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FabricCanvas } from '../../types/fabric';
import { getOrCreateGuestSessionId } from '../../lib/guestSession';
import { safeGetItem } from '../../lib/safeStorage';
import { useToast } from '../Notifications';
import { BackgroundColorTool } from './BackgroundColorTool';
import type { StagedImage } from './StagedImage';
import { Spinner } from '../Spinner';

interface UploadToolProps {
  canvas: FabricCanvas | null;
}

export function UploadTool({ canvas }: UploadToolProps) {
  const { t } = useTranslation();
  const showToast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  // Holds the just-uploaded image WITHOUT placing it on the design canvas —
  // per direct user request, uploading no longer drops the image straight
  // onto the placeholder. Instead this panel's drop-zone swaps to a preview +
  // the step-by-step editing wizard (BackgroundColorTool), and the image only
  // becomes a real canvas object once the wizard finishes (see that
  // component's `commitToCanvas`, invoked via the `onDone` callback below).
  const [staged, setStaged] = useState<StagedImage | null>(null);
  // Drives the drop-zone's own loading state while the upload request is in
  // flight — previously there was no feedback at all here, just a static
  // drop-zone sitting unchanged until the fetch resolved.
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      const token = safeGetItem('session', 'customedge_token');
      const formData = new FormData();
      formData.append('image', file);

      const res = await fetch('/api/uploads/image', {
        method: 'POST',
        // X-Session-Id namespaces this guest's upload storage folder on the
        // backend (uploads.ts) so this image's derived variants (vectorize,
        // remove-background, recolor, ...) don't collide with other guests'
        // uploads sharing the literal 'guest' folder — see guestSession.ts.
        // Sent unconditionally: the backend already prefers the logged-in
        // customer id over this header when both are present, so it's a no-op
        // for authenticated uploads.
        headers: { 'X-Session-Id': getOrCreateGuestSessionId(), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: formData,
      });

      if (!res.ok) {
        // Surface the backend's real error (e.g. "File type 'application/xml'
        // not allowed", "Image exceeds the maximum allowed size of 10 MB",
        // "Could not process this HEIC file — it may be corrupt or
        // unsupported") instead of a one-size-fits-all "Upload failed" —
        // reported live as making a real SVG-upload regression (see
        // uploads.ts / IMPLEMENTATION-STATUS.md row 1.16) impossible to
        // diagnose from the UI alone, and the same gap would hide the
        // actual reason for any future upload rejection just as well. Falls
        // back to the generic message only if the response body isn't the
        // expected `{ error }` JSON shape every route in uploads.ts returns.
        const message = await res.json().then(body => body?.error).catch(() => null);
        showToast(message || t('upload.failed'), 'error');
        return;
      }
      const { url, id, width, height, density } = await res.json();

      // Stage it — the drop-zone below now renders the wizard/preview instead
      // of "Click or drop an image here" until the wizard completes.
      setStaged({ uploadId: id, originalUrl: url, currentUrl: url, originalWidth: width, originalHeight: height, originalDensity: density });
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const cancelStaged = () => setStaged(null);

  return (
    <div style={{ padding: 12 }}>
      {/* Customer-token paste box previously lived here — removed per direct
          user request, since it pushed the actual upload preview/wizard
          below the fold and its normal use case (token normally arrives
          automatically via URL param / accessToken exchange, see
          useDesignerInit.ts) never needed a permanent visible box in this
          panel. safeGetItem('session', 'customedge_token') below is
          unaffected — plenty of other flows (autosave, save/load, cart,
          admin role detection) still set it the normal way. */}
      {!staged ? (
        <div onDrop={uploading ? undefined : onDrop} onDragOver={e => e.preventDefault()}
          onClick={() => {
            if (uploading) return; // request already in flight — ignore extra clicks instead of firing a second upload
            // A file <input>'s value isn't cleared after picking a file, so
            // selecting the SAME file again (e.g. re-adding an image the user
            // just removed from the canvas) doesn't change the input's value —
            // the browser then never fires a new 'change' event, and
            // handleFile silently never runs a second time. Clearing the
            // value right before opening the picker guarantees a 'change'
            // fires even when the user picks the identical file again.
            if (inputRef.current) inputRef.current.value = '';
            inputRef.current?.click();
          }}
          style={{
            border: '2px dashed var(--border)', borderRadius: 8, padding: 32, textAlign: 'center',
            cursor: uploading ? 'default' : 'pointer', background: '#f8fafc',
          }}>
          {uploading ? (
            <>
              <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center' }}><Spinner size={28} color="var(--selection)" /></div>
              <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>{t('upload.uploading')}</p>
            </>
          ) : (
            <>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📁</div>
              <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>{t('upload.dropHere')}</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{t('upload.formats')}</p>
            </>
          )}
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, background: '#f8fafc' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{t('upload.editingUpload')}</span>
            <button onClick={cancelStaged} title={t('upload.cancelUpload')}
              style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 2 }}>
              ×
            </button>
          </div>
          {/* key={staged.uploadId} forces a full remount whenever a NEW image
              is staged (uploading a second image while mid-wizard on the
              first) — without this, BackgroundColorTool's internal state
              (previewUrl, stepIndex, wizardDone, colors, cropInsets,
              rotation, hasEdits — all plain useState, none derived from
              props on every render) survives the prop change untouched,
              since React only re-renders the existing instance rather than
              creating a new one. Confirmed live: uploading image #2 while
              image #1 was sitting at step 2 (post Remove Background) left
              the wizard showing image #1's edited preview and wizard
              progress while every subsequent API call silently targeted
              image #2's uploadId — a real stale-state bug, not a caching
              issue on the backend. */}
          <BackgroundColorTool key={staged.uploadId} canvas={canvas} staged={staged} onDone={() => setStaged(null)} />
        </div>
      )}

      <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.svg,.pdf,.ai,.eps,.heic,.cdr" style={{ display: 'none' }}
        onChange={e => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          // Also clear after handling, in case a future change stops clearing
          // it pre-emptively on click (e.g. a keyboard-triggered file pick
          // that bypasses the onClick handler above).
          e.target.value = '';
        }} />
    </div>
  );
}
